/**
 * A contextual panel rail scoped to ONE WORKBENCH (#1370 QB1, ruling 48391).
 *
 * ── what this is, and the thing it is deliberately NOT ──────────────────────
 * `DrawerShell` is the application's global right drawer: one per app, bound to
 * whatever record the current route registered, one persisted open tenant, a
 * launcher in the shell's safe area. Its panels are lenses ABOUT THE RECORD ON
 * SCREEN.
 *
 * This is a rail INSIDE a workbench — a quote builder, and later any other
 * dense editing canvas. Its panels are lenses on THE SUBJECT THAT WORKBENCH IS
 * EDITING: for CPQ, Catalog targets the quote root or a nested group.
 *
 * The two have similar mechanics and different identity, and the test that
 * settles it is that **they must coexist**. A quote workbench is a page; the
 * shell drawer is the shell; both are on screen at once. Generalising one into
 * the other would give them one open tenant, one URL slot and one launcher
 * between them, and the bug would read as "opening Catalog closed my Timeline".
 * That mistake was proposed and corrected before any code (mailbox 48384/48388)
 * — recorded here because the reasoning is what stops it being re-proposed.
 *
 * ── CONTROLLED, and owning no ambient state ─────────────────────────────────
 * `activePanel` / `onActivePanelChange`, and that is the whole of it. This
 * component reads no route, no `localStorage` and no global drawer context. It
 * cannot: a workbench may be mounted twice on one screen (a compare view), so
 * anything ambient would be shared between instances that are supposed to be
 * independent.
 *
 *   · the HOST ADAPTER decides whether the active panel lives in the URL;
 *   · SOLUTION EXPERIENCE DATA decides which panels exist, their order and the
 *     default — through the ordinary platform → persona → user cascade;
 *   · `subject` is for accessibility, focus and test identity ONLY. It is not a
 *     storage key and must not become one.
 *
 * ── what it reuses, and what it pointedly does not ──────────────────────────
 * REUSES the exported `Splitter`, `SplitGeometry`, `clampToGeometry` and
 * `resolvePreferred` (#1747/#1750), and the measured responsive mechanics from
 * #1465: a 44×44 target floor, a panel clamp, and an overlay decided by whether
 * the PRIMARY CANVAS would drop under its floor rather than by a viewport
 * number somebody chose.
 *
 * REUSES NONE of `DrawerShell`'s state, storage, context or breakpoint
 * constants. Sharing mechanics is the point; sharing state is the defect.
 */
import {
  useCallback, useEffect, useId, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import {
  Splitter, clampToGeometry, resolvePreferred,
  type SplitGeometry,
} from '../shell/WorkspaceSplit'
import s from './WorkbenchPanelRail.module.css'

/**
 * The panel's body state, made finite so a workbench cannot invent a fifth.
 *
 * A panel is the place a governed read is most likely to be refused — Pricing
 * on a quote you may see but not price, Audit without the privilege — and
 * "refused" is not "empty". A rail that renders both as a blank body tells the
 * person their data is missing when it is actually withheld.
 */
export type PanelBodyState =
  | { kind: 'loading' }
  | { kind: 'ready'; body: ReactNode }
  | { kind: 'empty'; message: string }
  /** Some of it arrived. The part that did is still worth showing. */
  | { kind: 'partial'; body: ReactNode; message: string }
  /** Authorisation said no. Say so, and say what it was. */
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

export interface WorkbenchPanelContext {
  /** What this workbench is editing, for the panel's own reads. */
  subject: WorkbenchSubject
  /** Ask the rail to close this panel — e.g. after the panel's own act. */
  close: () => void
}

export interface WorkbenchPanel {
  /** Stable semantic key. Also the host's URL token, if the host wants one. */
  key: string
  label: string
  /** A glyph or node. The rail renders it at the ruled 20–22px. */
  icon: ReactNode
  /**
   * An attention count, e.g. Validations with 3 errors. `tone` decides the
   * badge's colour; the COUNT is what a screen reader is told, because "3
   * findings" is information and a red dot is decoration.
   */
  attention?: { count: number; tone?: 'info' | 'warn' | 'critical' }
  /**
   * The capability this panel needs. Omitted = always visible. Checked against
   * `permitted`; `null` permitted means "this host has no capability model".
   */
  capability?: string
  /** Lazy: called only while the panel is the active one. */
  render: (ctx: WorkbenchPanelContext) => PanelBodyState
}

/** Identity for accessibility, focus and tests. NOT a persistence key. */
export interface WorkbenchSubject {
  /** Stable id of the thing being edited — a quote id, say. */
  id: string
  /** What to call it out loud: "Quote Q-1042". */
  label: string
}

export interface WorkbenchPanelRailProps {
  /** In rail order. The SOLUTION decides this list and its order. */
  panels: readonly WorkbenchPanel[]
  /** Capability keys the caller holds, or `null` for "no capability model". */
  permitted?: readonly string[] | null
  subject: WorkbenchSubject
  /** Controlled. `null` = no panel open. */
  activePanel: string | null
  onActivePanelChange: (key: string | null) => void
  /** The workbench's primary canvas. */
  children: ReactNode
  /**
   * Controlled panel width in px, or `null` to use the geometry's default.
   * The HOST owns persistence — this component deliberately stores nothing.
   */
  panelWidth?: number | null
  onPanelWidthChange?: (width: number | null) => void
  /** Override the default geometry. Rarely needed; the default is the ruled one. */
  geometry?: SplitGeometry
  /**
   * The primary canvas may not be squeezed below this. When it would be, the
   * panel overlays instead of docking. Derived, not chosen — see #1465.
   */
  canvasFloorPx?: number
  testId?: string
}

/**
 * The ruled panel geometry (#1465 46696): `clamp(420px, 30vw, 640px)`.
 *
 * The same numbers as the drawer, and that is a shared MEASUREMENT rather than
 * shared state — both are a dense secondary surface beside a primary canvas, so
 * the answer to "how wide before the canvas suffers" is the same answer.
 */
export const WORKBENCH_PANEL_GEOMETRY: SplitGeometry = {
  unit: 'px',
  min: 420,
  max: 640,
  preferred: { kind: 'viewport', fraction: 0.3 },
  step: 16,
  coarseStep: 64,
}

/** The canvas floor the overlay decision is derived from (#1465, ruling 47851 B2). */
export const WORKBENCH_CANVAS_FLOOR_PX = 560

/** Panels this caller may see. `null` permitted = no capability model at all. */
export function permittedPanels(
  panels: readonly WorkbenchPanel[], permitted: readonly string[] | null | undefined,
): readonly WorkbenchPanel[] {
  if (permitted == null) return panels
  const held = new Set(permitted)
  return panels.filter((p) => !p.capability || held.has(p.capability))
}

export function WorkbenchPanelRail({
  panels, permitted = null, subject, activePanel, onActivePanelChange, children,
  panelWidth = null, onPanelWidthChange,
  geometry = WORKBENCH_PANEL_GEOMETRY,
  canvasFloorPx = WORKBENCH_CANVAS_FLOOR_PX,
  testId = 'workbench',
}: WorkbenchPanelRailProps) {
  const uid = useId()
  const visible = useMemo(() => permittedPanels(panels, permitted), [panels, permitted])

  /* A remembered panel the caller may no longer see. Closing is the honest
     answer — silently opening a different one would put them somewhere they did
     not ask to be, and leaving it open would render a panel the capability
     model says they cannot have. */
  const active = visible.find((p) => p.key === activePanel) ?? null
  useEffect(() => {
    if (activePanel !== null && !visible.some((p) => p.key === activePanel)) onActivePanelChange(null)
  }, [activePanel, visible, onActivePanelChange])

  /* ── the overlay decision, measured on THIS workbench ──────────────────────
   * Not a viewport media query: a workbench can be 700px wide inside a 1600px
   * window (beside the shell's own drawer, which is exactly the coexistence
   * this component has to survive). The question is whether the PRIMARY CANVAS
   * would fall under its floor, and only this box can answer it. */
  const frame = useRef<HTMLDivElement>(null)
  const [frameWidth, setFrameWidth] = useState<number | null>(null)
  useEffect(() => {
    const el = frame.current
    if (!el) return
    const measure = () => { if (el.clientWidth > 0) setFrameWidth(el.clientWidth) }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const preferred = resolvePreferred(
    geometry, typeof window === 'undefined' ? 0 : window.innerWidth)
  const width = panelWidth === null ? preferred : clampToGeometry(geometry, panelWidth)

  /* `null` frameWidth is "not measured yet" and docks: rendering an overlay for
     one frame and then snapping to docked is worse than the reverse. */
  const railPx = 61   // 44 target + 2×8 padding + 1px border — see the stylesheet
  const overlay = frameWidth !== null && (frameWidth - railPx - geometry.min) < canvasFloorPx

  /* ── focus ────────────────────────────────────────────────────────────────
   * The rail button that opened a panel is where focus goes when it closes.
   * Without this, closing drops focus to the body and a keyboard user is
   * returned to the top of the document from the middle of their work. */
  const opener = useRef<HTMLButtonElement | null>(null)
  const railNode = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    onActivePanelChange(null)
    const target = opener.current
    if (target?.isConnected) target.focus()
    else railNode.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [onActivePanelChange])

  const select = (key: string, trigger: HTMLButtonElement) => {
    opener.current = trigger
    if (activePanel === key) { close(); return }
    onActivePanelChange(key)
  }

  /* Roving arrows within the rail, which is what a toolbar of lenses owes a
     keyboard: Tab reaches the rail once, arrows choose inside it. */
  const railKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(event.key)) return
    const buttons = [...(railNode.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])]
    if (buttons.length === 0) return
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    event.preventDefault()
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? buttons.length - 1
        : event.key === 'ArrowDown' ? Math.min(buttons.length - 1, at + 1)
          : Math.max(0, at - 1)
    buttons[next]?.focus()
  }

  const panelId = `${testId}-panel-${uid}`
  const body = active?.render({ subject, close }) ?? null

  return (
    <div ref={frame} className={s.frame} data-testid={testId}
      data-overlay={overlay || undefined}
      data-open={active ? active.key : undefined}>
      <div className={s.canvas}>{children}</div>

      {/* The separator is #1750's, not a third drag implementation. Absent when
          overlaying: an overlay is not trading width with the canvas. */}
      {active && !overlay && onPanelWidthChange && (
        <Splitter
          geometry={geometry}
          size={width}
          preferred={preferred}
          container={frame}
          subject={`${active.label} panel`}
          onPreview={(next) => onPanelWidthChange(next)}
          onCommit={(next) => onPanelWidthChange(next)}
          onReset={() => onPanelWidthChange(null)}
        />
      )}

      {active && (
        <section className={s.panel} id={panelId}
          /* Focusable-but-not-tabbable, the same as the shell drawer's panel.
             Without it the section cannot receive a key event at all, so the
             Escape handler below is unreachable except by luck — measured in
             the browser, where `press('Escape')` on a non-focusable element
             sends the key to the body. */
          tabIndex={-1}
          style={overlay ? undefined : { width: `${width}px` }}
          aria-label={`${active.label} — ${subject.label}`}
          data-testid={`${testId}-panel`}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return
            event.preventDefault(); event.stopPropagation(); close()
          }}>
          <header className={s.head}>
            <h2 className={s.title}>{active.label}</h2>
            <button type="button" className={s.close} onClick={close}
              data-testid={`${testId}-panel-close`}
              aria-label={`Close ${active.label}`}>×</button>
          </header>
          <div className={s.body} data-state={body?.kind}>
            <PanelBody state={body} />
          </div>
        </section>
      )}

      <div ref={railNode} className={s.rail} role="toolbar" aria-orientation="vertical"
        aria-label={`${subject.label} panels`} onKeyDown={railKeys}
        data-testid={`${testId}-rail`}>
        {visible.map((p) => (
          <button key={p.key} type="button" className={s.btn}
            data-testid={`${testId}-panel-${p.key}`}
            data-on={activePanel === p.key || undefined}
            aria-expanded={activePanel === p.key}
            aria-controls={activePanel === p.key ? panelId : undefined}
            title={p.label}
            aria-label={p.attention?.count
              ? `${p.label}, ${p.attention.count} needing attention`
              : p.label}
            onClick={(event) => select(p.key, event.currentTarget)}>
            <span className={s.icon} aria-hidden>{p.icon}</span>
            {p.attention && p.attention.count > 0 && (
              <span className={s.badge} data-tone={p.attention.tone ?? 'info'} aria-hidden>
                {p.attention.count > 99 ? '99+' : p.attention.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

/** The five finite states, rendered so that none of them is a blank box. */
function PanelBody({ state }: { state: PanelBodyState | null }) {
  if (!state) return null
  switch (state.kind) {
    case 'loading':
      return <p className={s.note} role="status">Loading…</p>
    case 'ready':
      return <>{state.body}</>
    case 'empty':
      return <p className={s.note}>{state.message}</p>
    case 'partial':
      return <>
        <p className={s.note} data-kind="partial" role="status">{state.message}</p>
        {state.body}
      </>
    case 'refused':
      /* Not an error and not empty. The person is not missing data; they are
         not permitted to see it, and saying so is the difference between a
         broken panel and a governed one. */
      return <p className={s.note} data-kind="refused" role="status">{state.message}</p>
    case 'error':
      return <p className={s.note} data-kind="error" role="alert">{state.message}</p>
  }
}
